import React, { useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { canWrite } from "../utils/permissions";
import RichEditor from "../components/RichEditor";
import Layout from "../components/Layout";
import CategorySelect from "../components/CategorySelect";

const API = "https://koufnere5g.execute-api.eu-west-2.amazonaws.com/prod/articles";

export default function Write() {
  const { user, userType, getIdToken } = useContext(AuthContext);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [summary, setSummary] = useState("");
  const [categories, setCategories] = useState([]);
  const [contentJson, setContentJson] = useState(null);
  const [contentHtml, setContentHtml] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if user has permission to write
  if (!canWrite(userType)) {
    return (
      <Layout>
        <div className="p-4 flex items-center justify-center h-full">
          <div className="text-xl text-gray-500">
            You need a journalist account to write articles.
          </div>
        </div>
      </Layout>
    );
  }

  const handleEditorChange = (json, html) => {
    setContentJson(json);
    setContentHtml(html);
  };

  const handleSubmit = async (isDraft = false) => {
    if (!user) {
      setStatus("Please sign in to publish");
      return;
    }

    if (!title || !slug || !summary || !contentJson || !contentHtml) {
      setStatus("Please fill in all fields");
      return;
    }

    setLoading(true);
    setStatus("Publishing...");

    try {
      const idToken = getIdToken();
      if (!idToken) {
        throw new Error("No authentication token available");
      }

      const response = await fetch(
        isDraft ? `${API}/${slug}?draft=true` : API,
        {
          method: isDraft ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            title,
            slug,
            summary,
            contentJson,
            contentHtml,
            author: user.username,
            categories, // Add categories to the article data
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      setStatus(isDraft ? "Draft saved!" : "Published!");
      
      if (!isDraft) {
        // Clear form after publishing
        setTitle("");
        setSlug("");
        setSummary("");
        setCategories([]);
        setContentJson(null);
        setContentHtml("");
      }
    } catch (error) {
      console.error("Error publishing:", error);
      setStatus(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 max-w-4xl mx-auto">
        <h1 className="text-3xl font-serif mb-6">Write an Article</h1>

        {status && (
          <div className="mb-4 p-2 bg-taupe-light rounded">{status}</div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Article title"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block mb-1">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="article-slug"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block mb-1">Summary</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full p-2 border rounded"
              rows="2"
              placeholder="Brief summary"
              disabled={loading}
            ></textarea>
          </div>

          <div>
            <label className="block mb-1">Categories</label>
            <CategorySelect 
              selectedCategories={categories}
              onChange={setCategories}
            />
          </div>

          <div>
            <label className="block mb-1">Content</label>
            <RichEditor
              initialContent={contentJson}
              onChange={handleEditorChange}
            />
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => handleSubmit(true)}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              disabled={loading || !user}
              type="button"
            >
              Save Draft
            </button>
            <button
              onClick={() => handleSubmit(false)}
              className="px-4 py-2 bg-taupe text-white rounded disabled:opacity-50"
              disabled={loading || !user}
              type="button"
            >
              Publish
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}